/**
 * @fileoverview Hook to fetch linked payments for an indirect cost.
 * @module features/indirect-costs/hooks/useLinkedPayments
 */

import { useQuery } from '@tanstack/react-query'
import { fetchLinkedPayments } from '../api/indirectCostsLinksService'
import type { IndirectCostLinksResponse } from '../schemas/indirectCostsSchema'

/**
 * Hook to fetch linked payments for a specific indirect cost.
 *
 * @param costUuid The UUID of the indirect cost
 * @returns Query result containing linked payments information
 */
export function useLinkedPayments(
  costUuid: string | null
): ReturnType<typeof useQuery<IndirectCostLinksResponse, Error>> {
  return useQuery<IndirectCostLinksResponse, Error>({
    queryKey: ['indirect-costs', 'linked-payments', costUuid],
    queryFn: () => {
      if (!costUuid) {
        throw new Error('Cost UUID is required')
      }
      return fetchLinkedPayments(costUuid)
    },
    enabled: Boolean(costUuid),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
